import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import {
  CategoryName,
  DisplayModeName,
  TDatabaseId,
  tg,
} from '@infotech-soe/metaschema';

import { AppDispatch } from '../..';
import { DatasetParamValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import { BuiltinCaptionType } from '../../../models/caption/Caption';
import { ICategoryTreeNode } from '../../../models/category/categoryTree';
import { RefreshStrategy } from '../../../services/commands/wrap/CategoryCommand';
import HierarchyService from '../../../services/dm-trees/HierarchyService';
import IHierarchyService from '../../../services/dm-trees/IHierarchyService';
import { TreeFormFieldNames } from '../../../services/form/schema-factory/TreeFormSchemaFactory';
import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../reducers/root';
import { getCatalogsStateEntity } from '../../selectors/catalogs';
import {
  getCategoryMasterDataset,
  getCategoryState,
  getPendingLoadConfig,
} from '../../selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetActiveRowId,
} from '../../selectors/dataset';
import { getDictionaryById } from '../../selectors/dictionary';
import { getFormCommandName } from '../../selectors/form';
import {
  getHierarchyStateEntity,
  isHierarchyConnectedToForm,
} from '../../selectors/hierarchy';
import categoryActions, { CategoryTargetingInfo } from '../../slices/category';
import dataset from '../../slices/dataset';
import form from '../../slices/form';
import hierarchy, { HierarchyStateEntity } from '../../slices/hierarchy';
import {
  apiCall,
  createAppListener,
  postDispatch,
  postDispatchAny,
} from '../listenerHelpers';

const hierarchyListener = createAppListener();

const hierarchyService: IHierarchyService = new HierarchyService();

const getTreeState = (
  state: AppState,
  categoryId: UniqueId
): HierarchyStateEntity => getHierarchyStateEntity(state, categoryId);

const getCategoryStateByTree = (state: AppState, treeId: UniqueId) => {
  const { categoryId } = getTreeState(state, treeId);
  return getCategoryState(state, categoryId);
};

const getCategoryName = (state: AppState, treeId: UniqueId): CategoryName => {
  const { source } = getCategoryStateByTree(state, treeId);
  return source.category;
};

const getDisplayModeName = (
  state: AppState,
  treeId: UniqueId
): DisplayModeName => {
  const categoryState = getCategoryStateByTree(state, treeId);
  return categoryState.source.displayMode;
};

const getTreeParams = (
  state: AppState,
  treeId: UniqueId,
  params?: DatasetParamValues
): DatasetParamValues => params ?? (getTreeState(state, treeId).params || {});

const findNode = (
  state: AppState,
  categoryId: UniqueId,
  nodeId: TDatabaseId
): ICategoryTreeNode => {
  const tree = getTreeState(state, categoryId);
  const node = tree.nodes.find(n => n.id === nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} does not exist`);
  }
  return node;
};

const nodeHasChildren = (
  state: AppState,
  categoryId: UniqueId,
  nodeId: TDatabaseId
): boolean => findNode(state, categoryId, nodeId).hasChildren;

const childrenAreLoaded = (
  state: AppState,
  categoryId: UniqueId,
  nodeId: TDatabaseId
): boolean => getTreeState(state, categoryId).childrenCache.has(nodeId);

const shouldLoadChildren = (
  state: AppState,
  categoryId: UniqueId,
  nodeId: TDatabaseId
): boolean =>
  nodeHasChildren(state, categoryId, nodeId) &&
  !childrenAreLoaded(state, categoryId, nodeId);

const getActiveNodeId = (
  state: AppState,
  categoryId: UniqueId
): TDatabaseId | null => getTreeState(state, categoryId).activeId;

const getProvidedOrActiveNodeId = (
  state: AppState,
  categoryId: UniqueId,
  nodeId?: TDatabaseId
): TDatabaseId => {
  if (nodeId !== undefined) {
    return nodeId;
  }
  const activeId = getTreeState(state, categoryId).activeId;
  if (activeId === null) {
    throw new Error('Invalid state');
  }
  return activeId;
};

const isInCheckMode = (state: AppState, categoryId: UniqueId): boolean => {
  const tree = getTreeState(state, categoryId);
  return tree.checkedNodes.size > 0 && tree.hierarchyRootId === null;
};

const isInHierarchyMode = (state: AppState, categoryId: UniqueId): boolean =>
  getTreeState(state, categoryId).hierarchyRootId !== null;

function* getLoadedDatasets(state: AppState, categoryId: UniqueId) {
  const category = getCategoryState(state, categoryId);
  const loadedDatasets = filterDatasetsForCategory(state, categoryId);
  for (const ds of Object.values(category.datasets)) {
    if (loadedDatasets[ds.id] !== undefined) {
      yield ds;
    }
  }
}

const forEachRootDataset = (
  state: AppState,
  categoryId: UniqueId,
  callback: (datasetId: UniqueId) => void
): void => {
  for (const { parent, id } of getLoadedDatasets(state, categoryId)) {
    if (parent === null) {
      callback(id);
    }
  }
};

const updateRootDatasets = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId,
  targetingInfo?: CategoryTargetingInfo,
  forceDOMFocus?: true
): void => {
  forEachRootDataset(state, categoryId, datasetId => {
    dispatch(dataset.updateCatalogContext({ datasetId }));
    dispatch(dataset.update({ datasetId, targetingInfo, forceDOMFocus }));
  });
};

const updateDatasetsCatalogContext = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId
): void => {
  forEachRootDataset(state, categoryId, datasetId => {
    dispatch(dataset.updateCatalogContext({ datasetId }));
  });
};

const updateDatasetsAfterCatalogChange = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId,
  targetingInfo?: CategoryTargetingInfo,
  forceDOMFocus?: true
): void => {
  if (
    !isInCheckMode(state, categoryId) &&
    !isInHierarchyMode(state, categoryId)
  ) {
    updateRootDatasets(
      state,
      dispatch,
      categoryId,
      targetingInfo,
      forceDOMFocus
    );
  } else {
    updateDatasetsCatalogContext(state, dispatch, categoryId);
  }
};

const expandPathsToNodes = async (
  getState: () => AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId,
  nodeIds: TDatabaseId[],
  params?: DatasetParamValues
): Promise<void> => {
  const categoryName = getCategoryName(getState(), categoryId);
  const displayModeName = getDisplayModeName(getState(), categoryId);
  const treeParams = getTreeParams(getState(), categoryId, params);

  for (const nodeId of nodeIds) {
    try {
      const pathIds = await hierarchyService.getPath(
        categoryName,
        displayModeName,
        nodeId
      );

      for (const parentId of pathIds) {
        if (shouldLoadChildren(getState(), categoryId, parentId)) {
          const children = await hierarchyService.loadChildren(
            categoryName,
            displayModeName,
            parentId,
            treeParams
          );
          dispatch(
            hierarchy.loadChildrenSuccess({
              categoryId,
              parentId,
              nodes: children,
            })
          );
        }
        dispatch(hierarchy.expandNode({ categoryId, nodeId: parentId }));
      }
    } catch {
      // Ignore errors for individual nodes
    }
  }
};

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.loadRoots,
      success: hierarchy.loadRootsSuccess,
      failure: hierarchy.loadRootsFailure,
    },
    async ({ categoryId, targetingInfo, forceDOMFocus, params }, state) => {
      const nodes = await hierarchyService.loadRoots(
        getCategoryName(state, categoryId),
        getDisplayModeName(state, categoryId),
        params
      );
      return { categoryId, params, nodes, targetingInfo, forceDOMFocus };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.loadRootsSuccess,
    (
      { categoryId, nodes, targetingInfo, forceDOMFocus, params },
      state,
      dispatch,
      _prevState,
      getState
    ) => {
      if (nodes.length === 0) {
        return;
      }

      const dictionary = getDictionaryById(state, categoryId);
      const { source } = getCategoryState(state, categoryId);
      const fullHierarchy =
        dictionary?.fullHierarchy || source.inParams?.linksGraph;

      const pendingLoad = getPendingLoadConfig(state, categoryId);
      const treeCheckedIds = pendingLoad?.treeCheckedIds;

      if (treeCheckedIds) {
        dispatch(
          hierarchy.restoreChecks({ categoryId, nodeIds: treeCheckedIds })
        );

        const rootNodeIds = new Set(nodes.map(n => n.id));
        const nonRootCheckedIds = treeCheckedIds.filter(
          nodeId => !rootNodeIds.has(nodeId)
        );

        void expandPathsToNodes(
          getState,
          dispatch,
          categoryId,
          nonRootCheckedIds,
          params
        );
      }

      if (targetingInfo?.relatedWithTree && !fullHierarchy) {
        dispatch(
          hierarchy.locateNode({
            categoryId,
            nodeId: targetingInfo.relatedWithTree.entityId,
            targetingInfo,
            forceDOMFocus,
            params: getTreeParams(state, categoryId, params),
          })
        );
      } else {
        const firstNode = nodes[0];
        dispatch(
          hierarchy.selectNode({
            categoryId,
            nodeId: firstNode.id,
            forceDOMFocus,
            fullHierarchy,
          })
        );

        if (firstNode.hasChildren) {
          dispatch(hierarchy.expandNode({ categoryId, nodeId: firstNode.id }));
        }
        if (fullHierarchy) {
          dispatch(
            hierarchy.setHierarchyRoot({ categoryId, nodeId: firstNode.id })
          );
        }
      }

      if (treeCheckedIds) {
        updateRootDatasets(state, dispatch, categoryId, targetingInfo);
      }

      if (pendingLoad) {
        dispatch(categoryActions.clearPendingLoad({ categoryId }));
      }
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.expandNode,
    ({ categoryId, nodeId }, state, dispatch) => {
      const parentId = getProvidedOrActiveNodeId(state, categoryId, nodeId);
      if (shouldLoadChildren(state, categoryId, parentId)) {
        dispatch(hierarchy.loadChildren({ categoryId, parentId }));
      }
    }
  )
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.loadChildren,
      success: hierarchy.loadChildrenSuccess,
      failure: hierarchy.loadChildrenFailure,
    },
    async ({ categoryId, parentId }, state, dispatch) => {
      dispatch(hierarchy.markNodePending({ categoryId, nodeId: parentId }));

      const nodes = await hierarchyService.loadChildren(
        getCategoryName(state, categoryId),
        getDisplayModeName(state, categoryId),
        parentId,
        getTreeParams(state, categoryId)
      );

      return { categoryId, parentId, nodes };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.loadChildrenSuccess,
    ({ categoryId, parentId }, _state, dispatch) => {
      dispatch(hierarchy.unmarkNodePending({ categoryId, nodeId: parentId }));
    }
  )
);

hierarchyListener.startListening(
  postDispatchAny(
    [hierarchy.selectNode, hierarchy.nextNode, hierarchy.prevNode],
    (
      payload: ReturnType<typeof hierarchy.selectNode>['payload'],
      state,
      dispatch,
      prevState,
      actionType
    ) => {
      const { categoryId } = payload;
      const { forceDOMFocus, fullHierarchy } =
        actionType === hierarchy.selectNode.type
          ? payload
          : { forceDOMFocus: undefined, fullHierarchy: false };

      const prevActiveId = getActiveNodeId(prevState, categoryId);
      const nextActiveId = getActiveNodeId(state, categoryId);
      const activeChanged = prevActiveId !== nextActiveId;

      if (
        actionType === hierarchy.nextNode.type ||
        actionType === hierarchy.prevNode.type
      ) {
        const { formId } = getHierarchyStateEntity(state, categoryId);

        if (formId) {
          const command = getFormCommandName(state, formId);

          if (command === BuiltinCaptionType.MOVE) {
            const { activeId } = getCatalogsStateEntity(state, categoryId);

            dispatch(
              form.update({
                formId,
                field: TreeFormFieldNames.TARGET_ID,
                value: activeId,
              })
            );
          }
        }
      }

      if (
        activeChanged &&
        !isHierarchyConnectedToForm(state, categoryId) &&
        !fullHierarchy
      ) {
        const targeting =
          actionType === hierarchy.selectNode.type
            ? payload.targetingInfo
            : undefined;

        updateDatasetsAfterCatalogChange(
          state,
          dispatch,
          categoryId,
          targeting,
          forceDOMFocus
        );
      }
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.setHierarchyRoot,
    ({ categoryId }, state, dispatch) => {
      updateRootDatasets(state, dispatch, categoryId);
    }
  )
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.toggleCheck,
      success: hierarchy.toggleCheckSuccess,
      failure: hierarchy.toggleCheckFailure,
    },
    async ({ categoryId, nodeId, shift }, state, dispatch) => {
      const categoryName = getCategoryName(state, categoryId);
      const displayModeName = getDisplayModeName(state, categoryId);
      const parentId = getProvidedOrActiveNodeId(state, categoryId, nodeId);

      const toggledIds: TDatabaseId[] = shift
        ? await hierarchyService.getRecursiveChildIds(
            categoryName,
            displayModeName,
            parentId,
            getTreeParams(state, categoryId)
          )
        : [parentId];

      if (isInHierarchyMode(state, categoryId)) {
        dispatch(
          hierarchy.hierarchyToChecks({ categoryId, excludedNodes: toggledIds })
        );
      } else {
        dispatch(
          hierarchy.toggleNodeChecked({ categoryId, parentId, toggledIds })
        );
        updateRootDatasets(state, dispatch, categoryId);
      }

      return { categoryId, nodeId, shift };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.collapseNode,
    ({ categoryId }, state, dispatch, prevState) => {
      const prevActive = getActiveNodeId(prevState, categoryId);
      const nextActive = getActiveNodeId(state, categoryId);

      if (
        nextActive !== prevActive &&
        !isHierarchyConnectedToForm(state, categoryId)
      ) {
        updateRootDatasets(state, dispatch, categoryId);
      }
    }
  )
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.locateNode,
      success: hierarchy.locateNodeSuccess,
      failure: hierarchy.locateNodeFailure,
    },
    async (
      { categoryId, nodeId, targetingInfo, forceDOMFocus, params },
      state
    ) => {
      const categoryName = getCategoryName(state, categoryId);
      const displayModeName = getDisplayModeName(state, categoryId);
      const treeId = targetingInfo?.relatedWithTree?.treeId;
      const treeSelectionId = tg.isNumber(treeId) ? treeId : nodeId;
      const treeParams = getTreeParams(state, categoryId, params);

      const roots = await hierarchyService.loadRoots(
        categoryName,
        displayModeName,
        treeParams
      );

      const pathIds = await hierarchyService.getPath(
        categoryName,
        displayModeName,
        treeSelectionId
      );

      const path: Array<[TDatabaseId, ITreeNodeInstance[]]> = [];

      for (const id of pathIds) {
        const nodes = await hierarchyService.loadChildren(
          categoryName,
          displayModeName,
          id,
          treeParams
        );
        path.push([id, nodes]);
      }

      return {
        categoryId,
        nodeId,
        roots,
        path,
        targetingInfo,
        treeSelectionId,
        forceDOMFocus,
        params: treeParams,
      };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.locateNodeSuccess,
    (
      { categoryId, targetingInfo, treeSelectionId, forceDOMFocus, path },
      state,
      dispatch
    ) => {
      const { nodes, refreshHierarchyRootId, refreshIsExpandedNode } =
        getTreeState(state, categoryId);

      const findInNodes = (
        nodeId?: TDatabaseId
      ): ICategoryTreeNode | undefined => nodes.find(({ id }) => id === nodeId);

      if (
        refreshHierarchyRootId !== null &&
        findInNodes(refreshHierarchyRootId)
      ) {
        dispatch(
          hierarchy.setHierarchyRoot({
            categoryId,
            nodeId: refreshHierarchyRootId,
          })
        );
      }

      const node = findInNodes(treeSelectionId);
      if (node) {
        dispatch(
          hierarchy.selectNode({
            categoryId,
            nodeId: treeSelectionId,
            targetingInfo,
            forceDOMFocus,
          })
        );

        if (refreshIsExpandedNode || node.parentId === null) {
          dispatch(
            hierarchy.expandNode({ categoryId, nodeId: treeSelectionId })
          );
        }
      } else {
        const lvl = path.find(([treeNodeId]) => findInNodes(treeNodeId));

        if (lvl) {
          dispatch(
            hierarchy.selectNode({ categoryId, nodeId: lvl[0], forceDOMFocus })
          );
        }
      }
    }
  )
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.refresh,
      success: hierarchy.refreshSuccess,
      failure: hierarchy.refreshFailure,
    },
    async ({ categoryId, loadPathToNode, params }, state, dispatch) => {
      const activeNode = loadPathToNode ?? getActiveNodeId(state, categoryId);
      const { activeId, hierarchyRootId, expandedNodes } =
        getHierarchyStateEntity(state, categoryId);
      const treeParams = getTreeParams(state, categoryId, params);

      dispatch(hierarchy.initializeTree({ categoryId }));

      if (activeNode !== null && activeId !== null) {
        const { id } = getCategoryMasterDataset(state, categoryId);
        const activeRowId = getDatasetActiveRowId(state, id) ?? 0;

        dispatch(
          hierarchy.locateNode({
            categoryId,
            nodeId: activeRowId,
            targetingInfo: {
              relatedWithTree: { treeId: activeNode, entityId: activeRowId },
            },
            params: treeParams,
          })
        );

        return {
          categoryId,
          hierarchyRootId,
          isExpandedNode: expandedNodes.includes(activeNode),
        };
      } else {
        dispatch(hierarchy.loadRoots({ categoryId, params: treeParams }));
      }

      return { categoryId };
    }
  )
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.hierarchyToChecks,
      success: hierarchy.hierarchyToChecksSuccess,
      failure: hierarchy.hierarchyToChecksFailure,
    },
    async ({ categoryId, excludedNodes }, state) => {
      const categoryName = getCategoryName(state, categoryId);
      const displayModeName = getDisplayModeName(state, categoryId);
      const tree = getTreeState(state, categoryId);

      if (tree.hierarchyRootId === null) {
        throw new Error('Not in hierarchy mode');
      }

      const childIds = await hierarchyService.getRecursiveChildIds(
        categoryName,
        displayModeName,
        tree.hierarchyRootId,
        getTreeParams(state, categoryId)
      );

      const isExcludedInHierarchyList = childIds.some(id =>
        excludedNodes.includes(id)
      );

      const checkedNodes = isExcludedInHierarchyList
        ? childIds.filter(id => !excludedNodes.includes(id))
        : excludedNodes;

      return { categoryId, checkedNodes, excludedNodes };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(hierarchy.clearChecked, ({ categoryId }, state, dispatch) => {
    updateRootDatasets(state, dispatch, categoryId);
  })
);

hierarchyListener.startListening(
  apiCall(
    {
      request: hierarchy.move,
      success: hierarchy.moveSuccess,
      failure: hierarchy.moveFailure,
    },
    async ({ categoryId, moveIds, newParentId }, state) => {
      const categoryName = getCategoryName(state, categoryId);

      for (const id of moveIds) {
        await hierarchyService.moveHierarchy(categoryName, {
          id,
          pid: newParentId,
        });
      }

      return { categoryId, moveIds };
    }
  )
);

hierarchyListener.startListening(
  postDispatch(
    hierarchy.moveSuccess,
    ({ categoryId, moveIds }, state, dispatch) => {
      forEachRootDataset(state, categoryId, datasetId => {
        dispatch(
          dataset.refreshWithStrategy({
            datasetId,
            strategy: RefreshStrategy.REMOVE,
            ids: moveIds,
          })
        );
      });

      dispatch(hierarchy.refresh({ categoryId }));
    }
  )
);

export default hierarchyListener.middleware;
